/**
 * UserFormSection Component
 * 사용자 정보 입력 폼 섹션
 */
import React, { useCallback } from 'react';
import { User, CreateUserRequestDto, UpdateUserRequestDto } from '../types/user.types';
import { formatPhoneNumber } from '@/shared/utils/formatter';
import { PRM_CODE, getPrmCodeLabel, USER_TYPE, getUserTypeLabel } from '../../shared/config/system.config';
import { getItem, STORAGE_KEYS } from '@/shared/utils/storage';
import { PartnerPortalLoginResponse } from '@/features/auth/types/auth.types';
import {
    FormDataType,
    FORM_LABELS,
    USE_YN_OPTIONS,
    extractPhoneNumbers,
} from './userDetailModal.helpers';

// ======== 타입 ========

interface UserFormSectionProps {
    formData: FormDataType;
    user: User | null;
    prmCodes: { comCode: string; comCdName: string; val3?: string | null }[];
    isCreateMode: boolean;
    onChange: (field: keyof (CreateUserRequestDto | UpdateUserRequestDto), value: string) => void;
}

// ======== 컴포넌트 ========

const UserFormSection: React.FC<UserFormSectionProps> = ({
                                                             formData,
                                                             user,
                                                             prmCodes,
                                                             isCreateMode,
                                                             onChange,
                                                         }) => {
    /**
     * 전화번호 변경 핸들러 (숫자만 추출하여 저장)
     */
    const handlePhoneChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const numbers = extractPhoneNumbers(e.target.value);
        onChange('phoneNum', numbers);
    }, [onChange]);

    // 현재 로그인한 사용자 정보 확인
    const adminYn = getItem<boolean>('ADMIN_YN');
    const userInfo = getItem<PartnerPortalLoginResponse>(STORAGE_KEYS.USER_INFO);
    const currentUserType = userInfo?.userType || getItem<string>('USER_TYPE');
    
    // 현재 로그인한 사용자가 관리자 또는 본사 사용자인 경우 권한 수정 가능
    const canEditPermission = !isCreateMode && 
        (adminYn === true || currentUserType === 'HEADQUARTERS');

    // 대상 사용자의 userType 확인 (비즈니스 로직용)
    const targetUserType = user?.userType;

    // 대상 사용자가 협력사(S) 타입인지 확인 (userType 기준)
    const isSupplierUserType = targetUserType === USER_TYPE.SUPPLIER;

    // 대상 사용자가 라이더(R) 타입인지 확인
    const isRiderUserType = targetUserType === USER_TYPE.RIDER;

    // 신규 생성 시 선택한 권한의 baseUserType 확인
    const getBaseUserTypeFromPrmCode = (prmCode: string): string | null => {
        const code = prmCodes.find(c => c.comCode === prmCode);
        if (!code) return null;
        
        // 표준 권한 코드인 경우
        if (prmCode === PRM_CODE.ADMIN || prmCode === PRM_CODE.HEAD || 
            prmCode === PRM_CODE.SUPPLIER || prmCode === PRM_CODE.RIDER) {
            return prmCode;
        }
        
        // SOD는 협력사 타입
        if (prmCode === PRM_CODE.SUPPLIER_ONLY_DASHBOARD) {
            return USER_TYPE.SUPPLIER;
        }
        
        // 커스텀 권한의 경우 val3(baseUserType) 반환
        return code.val3 || null;
    };

    // 협력사 타입 필드 표시 여부 (userType 기준)
    const shouldShowSuppType = isCreateMode 
        ? getBaseUserTypeFromPrmCode(formData.prmCode || '') === USER_TYPE.SUPPLIER
        : isSupplierUserType;

    /**
     * 권한 코드 필터링 함수
     * - 표준 권한(A, C, S, R, SOD)과 해당 userType에 맞는 커스텀 권한을 표시
     * @param code 권한 코드 객체
     */
    const filterPrmCode = (code: { comCode: string; comCdName: string; val3?: string | null }) => {
        // 표준 권한 코드 목록
        const standardCodes: string[] = [PRM_CODE.ADMIN, PRM_CODE.HEAD, PRM_CODE.SUPPLIER, PRM_CODE.RIDER, PRM_CODE.SUPPLIER_ONLY_DASHBOARD];
        const isStandardCode = standardCodes.includes(code.comCode);

        if (isSupplierUserType) {
            // 협력사(S) 사용자: 협력사 관련 표준 권한 + baseUserType이 S인 커스텀 권한
            if (isStandardCode) {
                return code.comCode === PRM_CODE.SUPPLIER || code.comCode === PRM_CODE.SUPPLIER_ONLY_DASHBOARD;
            }
            // 커스텀 권한: val3(baseUserType)가 'S'인 경우
            return code.val3 === USER_TYPE.SUPPLIER;
        }

        if (isRiderUserType) {
            // 라이더(R) 사용자: 라이더 권한 + baseUserType이 R인 커스텀 권한
            if (isStandardCode) {
                return code.comCode === PRM_CODE.RIDER;
            }
            return code.val3 === USER_TYPE.RIDER;
        }

        // 본사(C) 또는 관리자(A) 사용자: 본사/관리자 관련 표준 권한 + baseUserType이 C 또는 A인 커스텀 권한
        if (isStandardCode) {
            return code.comCode === PRM_CODE.HEAD ||
                code.comCode === PRM_CODE.ADMIN ||
                code.comCode === PRM_CODE.SUPPLIER_ONLY_DASHBOARD;
        }
        // 커스텀 권한: val3(baseUserType)가 'C' 또는 'A'인 경우
        return code.val3 === USER_TYPE.HEAD || code.val3 === USER_TYPE.ADMIN;
    };

    return (
        <div className="form-section">
            {/* 사용자 ID / 사용자명 */}
            <div className="form-row">
                <div className="form-group">
                    <label className="form-label">{FORM_LABELS.USER_ID}</label>
                    <input
                        type="text"
                        className="form-input"
                        value={isCreateMode ? (formData.userId || '') : (user?.userId || '')}
                        onChange={isCreateMode ? (e) => onChange('userId', e.target.value) : undefined}
                        disabled={!isCreateMode}
                        readOnly={!isCreateMode}
                        required={isCreateMode}
                    />
                </div>

                <div className="form-group">
                    <label className="form-label">{FORM_LABELS.USER_NAME}</label>
                    <input
                        type="text"
                        className="form-input"
                        value={formData.userName || ''}
                        onChange={(e) => onChange('userName', e.target.value)}
                        required={isCreateMode}
                    />
                </div>
            </div>

            {/* 협력사명 (라이더 계정인 경우만 표시) / 사용 여부 */}
            {!isCreateMode && user?.prmCode === PRM_CODE.RIDER && user?.suppName ? (
                <div className="form-row">
                    <div className="form-group">
                        <label className="form-label">협력사명</label>
                        <input
                            type="text"
                            className="form-input"
                            value={user.suppName}
                            disabled
                            readOnly
                        />
                    </div>
                    <div className="form-group">
                        <label className="form-label">{FORM_LABELS.USE_YN}</label>
                        <select
                            className="form-input"
                            value={formData.useYn}
                            onChange={(e) => onChange('useYn', e.target.value)}
                            required
                        >
                            {USE_YN_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            ) : null}

            {/* 사용자 타입 / 권한 */}
            <div className="form-row">
                <div className="form-group">
                    <label className="form-label">사용자 타입</label>
                    <input
                        type="text"
                        className="form-input"
                        value={user?.userTypeName || '-'}
                        disabled
                        readOnly
                    />
                </div>

                <div className="form-group">
                    <label className="form-label">{FORM_LABELS.PERMISSION}</label>
                    {isCreateMode ? (
                        <select
                            className="form-input"
                            value={formData.prmCode || ''}
                            onChange={(e) => onChange('prmCode', e.target.value)}
                            required
                        >
                            <option value="">선택</option>
                            {prmCodes
                                .filter(filterPrmCode)
                                .map((code) => (
                                    <option key={code.comCode} value={code.comCode}>
                                        {code.comCdName || getPrmCodeLabel(code.comCode)}
                                    </option>
                                ))}
                        </select>
                    ) : canEditPermission ? (
                        <select
                            className="form-input"
                            value={formData.prmCode || user?.prmCode || ''}
                            onChange={(e) => onChange('prmCode', e.target.value)}
                        >
                            <option value="">선택</option>
                            {prmCodes
                                .filter(filterPrmCode)
                                .map((code) => (
                                    <option key={code.comCode} value={code.comCode}>
                                        {code.comCdName || getPrmCodeLabel(code.comCode)}
                                    </option>
                                ))}
                        </select>
                    ) : (
                        <input
                            type="text"
                            className="form-input"
                            value={user?.prmCodeName || ''}
                            disabled
                            readOnly
                        />
                    )}
                </div>
            </div>

            {/* 사용 여부 / 협력사 타입 (라이더가 아닌 경우) */}
            {!(!isCreateMode && user?.prmCode === PRM_CODE.RIDER && user?.suppName) && (
                <div className="form-row">
                    <div className="form-group">
                        <label className="form-label">{FORM_LABELS.USE_YN}</label>
                        <select
                            className="form-input"
                            value={formData.useYn}
                            onChange={(e) => onChange('useYn', e.target.value)}
                            required
                        >
                            {USE_YN_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* 협력사 타입 (협력사 userType인 경우만 표시 - 커스텀 권한 포함) */}
                    {shouldShowSuppType ? (
                        <div className="form-group">
                            <label className="form-label">협력사 타입</label>
                            <select
                                className="form-input"
                                value={formData.suppType || 'D'}
                                onChange={(e) => onChange('suppType', e.target.value)}
                            >
                                <option value="D">직영</option>
                                <option value="F">가맹</option>
                            </select>
                        </div>
                    ) : (
                        <div className="form-group">
                            {/* 빈 공간 유지 */}
                        </div>
                    )}
                </div>
            )}

            {/* 전화번호 / 이메일 */}
            <div className="form-row">
                <div className="form-group">
                    <label className="form-label">{FORM_LABELS.PHONE}</label>
                    <input
                        type="text"
                        className="form-input"
                        value={formatPhoneNumber(formData.phoneNum || '')}
                        onChange={handlePhoneChange}
                        required={isCreateMode}
                    />
                </div>

                <div className="form-group">
                    <label className="form-label">{FORM_LABELS.EMAIL}</label>
                    <input
                        type="email"
                        className="form-input"
                        value={formData.emailInfo || ''}
                        onChange={(e) => onChange('emailInfo', e.target.value)}
                        required={isCreateMode}
                    />
                </div>
            </div>
        </div>
    );
};

export default UserFormSection;
